"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { PhoneInput } from "@/components/ui/phone-input";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import { valuesWithAppName } from "@/lib/i18n";
import type { User, UserRole } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { passwordSchema } from "@repo/utils";
import { AlertTriangleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod/v4";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(0),
  name: z.string().min(0).max(255),
  phone: z.string().min(0).max(255),
});

type FormValues = z.infer<typeof formSchema>;

export function SignupForm({
  defaultEmail,
  defaultName,
  defaultPhone,
  onDone,
  emailNonEditable,
  isPostOAuth,
  requirePhone,
  onUserLoaded,
  roles,
}: {
  defaultEmail?: string;
  defaultName?: string;
  defaultPhone?: string;
  onDone?: (user: User) => void | Promise<void>;
  emailNonEditable?: boolean;
  isPostOAuth?: boolean;
  requirePhone?: boolean;
  onUserLoaded?: (user: User) => void | Promise<void>;
  roles?: UserRole[];
}) {
  const t = useTranslations();

  const { user, loaded, reloadUser } = useCurrentUser();

  const signupMutation = api.auth.signup.useMutation();
  const updateUserMutation = api.user.update.useMutation();

  const { setApiErrorsToForm } = useErrorFormatter();

  const phoneRequired = requirePhone || !isPostOAuth;

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
      email: defaultEmail ?? "",
      password: "",
      name: defaultName ?? "",
      phone: defaultPhone ?? "",
    },
  });

  useEffect(() => {
    if (user && loaded) {
      onUserLoaded?.(user);
    }
  }, [user, loaded]);

  const onSignupSubmit: SubmitHandler<FormValues> = async ({
    email,
    password,
    name,
    phone,
  }) => {
    try {
      const { user } = await signupMutation.mutateAsync({
        email,
        password,
        name,
        phone,
        roles,
        callbackUrl: "/auth/verify",
      });
      onDone?.(user);
    } catch (err) {
      console.error(err);
      setApiErrorsToForm(err, form);
    }
  };

  const onUserUpdateSubmit: SubmitHandler<FormValues> = async ({
    name,
    phone,
  }) => {
    try {
      const payload: { name: string; phone?: string } = { name };
      if (phone?.trim()) {
        payload.phone = phone;
      }

      const { user } = await updateUserMutation.mutateAsync(payload);

      await reloadUser();
      onDone?.(user);
    } catch (err) {
      console.error(err);
      setApiErrorsToForm(err, form);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          isPostOAuth ? onUserUpdateSubmit : onSignupSubmit,
        )}
        className="space-y-4"
      >
        {form.formState.isSubmitted && form.formState.errors.root?.message && (
          <Alert variant="destructive">
            <AlertTriangleIcon className="size-6" />
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("saas.auth.signup.form.fields.email.label")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={emailNonEditable}
                  autoComplete="email"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isPostOAuth && (
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("saas.auth.signup.form.fields.password.label")}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    schema={passwordSchema}
                    showValidationFeedback={
                      form.formState.isSubmitted && !form.formState.isValid
                    }
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <div>{t("saas.auth.signup.form.fields.contactGroup")}</div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("saas.auth.signup.form.fields.name.label")}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="name"
                  placeholder={t(
                    "saas.auth.signup.form.fields.name.placeholder",
                  )}
                />
              </FormControl>
              <FormMessage />
              <FormDescription className="text-xs">
                {t("saas.auth.signup.form.fields.name.hint")}
              </FormDescription>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("saas.auth.signup.form.fields.phone.label")}
                {!phoneRequired && (
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    ({t("saas.auth.signup.form.fields.phone.optional", { defaultMessage: "optional" })})
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <PhoneInput
                  required={phoneRequired}
                  rawValue={field.value}
                  onBlur={field.onBlur}
                  onChange={({ countryCode, phoneNumber }) => {
                    const combined = `${countryCode} ${phoneNumber}`;
                    form.setValue("phone", combined);
                    field.onChange(combined);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="text-xs text-muted-foreground">
          {t.rich(
            "saas.auth.signup.legal",
            valuesWithAppName(t, {
              terms: (children) => (
                <Link href="/terms" className="underline">
                  {children}
                </Link>
              ),
              privacy: (children) => (
                <Link href="/privacy" className="underline">
                  {children}
                </Link>
              ),
            }),
          )}
        </div>

        <Button
          className="w-full"
          type="submit"
          disabled={form.formState.isSubmitting}
        >
          {t("saas.auth.signup.form.fields.submit")}
        </Button>
      </form>
    </Form>
  );
}

SignupForm.displayName = "SignupForm";
